import { onMount, onDestroy } from '@/modules/_'
import gsap from 'gsap'

let hoverTimeout: ReturnType<typeof setTimeout> | null = null

export default function (element: HTMLElement, dataset: DOMStringMap) {
  const type = element.getAttribute('data-type') || 'all'
  const items = element.querySelectorAll<HTMLElement>(
    '[data-directional-hover-item]'
  )

  const firstItem = items[0]

  const activeImage = document.querySelector(
    '[data-active-work="image"] img'
  ) as HTMLImageElement

  const mainImage = firstItem?.querySelector(
    '[data-column="name"] .work_catalogue_images_wrap img'
  ) as HTMLImageElement

  if (activeImage && mainImage) {
    activeImage.src = mainImage.src
  }

  const directionMap = {
    top: 'translateY(-100%)',
    bottom: 'translateY(100%)',
    left: 'translateX(-100%)',
    right: 'translateX(100%)',
  }

  const eventHandlers = new Map<
    HTMLElement,
    { enter: (e: MouseEvent) => void; leave: (e: MouseEvent) => void }
  >()

  function getDirection(
    event: MouseEvent,
    el: HTMLElement,
    type: string
  ): keyof typeof directionMap {
    const { left, top, width: w, height: h } = el.getBoundingClientRect()
    const x = event.clientX - left
    const y = event.clientY - top

    if (type === 'y') return y < h / 2 ? 'top' : 'bottom'
    if (type === 'x') return x < w / 2 ? 'left' : 'right'

    const distances = {
      top: y,
      right: w - x,
      bottom: h - y,
      left: x,
    }

    return Object.entries(distances).reduce((a, b) =>
      a[1] < b[1] ? a : b
    )[0] as keyof typeof directionMap
  }

  let currentPage: number = 1
  let totalPages: number = 1

  interface PaginationAPI {
    showPage: (page: number) => void
  }

  interface PageInfo {
    currentPage: number
    totalPages: number
    isFirstPage: boolean
    isLastPage: boolean
  }

  function initPagination(): PaginationAPI {
    const container = document.querySelector(
      '.work_catalogue_content_wrap'
    ) as HTMLElement
    const items = Array.from(
      container.querySelectorAll('.work_catalogue_entry')
    ) as HTMLElement[]
    const itemsPerPage = 9
    const countElement = document.querySelector(
      '.work_catalogue_count_text'
    ) as HTMLElement

    if (countElement) {
      countElement.textContent = items.length.toString()
    }

    totalPages = Math.ceil(items.length / itemsPerPage)

    function showPage(page: number): void {
      items.forEach((item) => {
        item.style.display = 'none'
      })

      const startIndex = (page - 1) * itemsPerPage
      const endIndex = startIndex + itemsPerPage

      items.slice(startIndex, endIndex).forEach((item) => {
        item.style.display = 'block'
      })
    }

    showPage(currentPage)

    const prevButton = document.querySelector(
      '[data-pagination="prev"]'
    ) as HTMLElement
    const nextButton = document.querySelector(
      '[data-pagination="next"]'
    ) as HTMLElement

    if (currentPage === 1) {
      prevButton?.classList.add('disabled')
    } else {
      prevButton?.classList.remove('disabled')
    }

    if (currentPage === totalPages) {
      nextButton?.classList.add('disabled')
    } else {
      nextButton?.classList.remove('disabled')
    }

    return { showPage }
  }

  function paginationControl(direction: string): PageInfo {
    if (direction === 'prev' && currentPage > 1) {
      currentPage--
    } else if (direction === 'next' && currentPage < totalPages) {
      currentPage++
    }

    const pagination = initPagination()
    pagination.showPage(currentPage)

    const prevButton = document.querySelector(
      '[data-pagination="prev"]'
    ) as HTMLElement
    const nextButton = document.querySelector(
      '[data-pagination="next"]'
    ) as HTMLElement

    if (currentPage === 1) {
      prevButton?.classList.add('disabled')
    } else {
      prevButton?.classList.remove('disabled')
    }

    if (currentPage === totalPages) {
      nextButton?.classList.add('disabled')
    } else {
      nextButton?.classList.remove('disabled')
    }

    return {
      currentPage: currentPage,
      totalPages: totalPages,
      isFirstPage: currentPage === 1,
      isLastPage: currentPage === totalPages,
    }
  }

  interface SortState {
    column: string | null
    direction: 'asc' | 'desc'
  }

  let currentSort: SortState = { column: null, direction: 'asc' }

  function initTableSorting(): void {
    const container = document.querySelector(
      '.work_catalogue_content_wrap.is--full'
    ) as HTMLElement

    document.addEventListener('click', function (e: Event) {
      const target = e.target as HTMLElement
      const headerCell = target.closest('[data-sort]') as HTMLElement
      if (headerCell && headerCell.dataset.sort) {
        const column = headerCell.dataset.sort
        sortTable(column, container)
      }
    })
  }

  function sortTable(column: string, container: HTMLElement): void {
    const items = Array.from(
      container.querySelectorAll('.work_catalogue_entry.is--content.w-dyn-item')
    ) as HTMLElement[]

    if (currentSort.column === column) {
      currentSort.direction = currentSort.direction === 'asc' ? 'desc' : 'asc'
    } else {
      currentSort.direction = 'asc'
    }
    currentSort.column = column

    const sortedItems = items.sort((a, b) => {
      let valueA: string | number, valueB: string | number

      switch (column) {
        case 'name':
          valueA = getColumnText(a, 0)
          valueB = getColumnText(b, 0)
          break
        case 'service':
          valueA = getColumnText(a, 1)
          valueB = getColumnText(b, 1)
          break
        case 'year':
          valueA = parseInt(getColumnText(a, 2)) || 0
          valueB = parseInt(getColumnText(b, 2)) || 0
          break
        case 'industry':
          valueA = getColumnText(a, 3)
          valueB = getColumnText(b, 3)
          break
        default:
          return 0
      }

      let comparison = 0
      if (column === 'year') {
        comparison = (valueA as number) - (valueB as number)
      } else {
        comparison = (valueA as string)
          .toLowerCase()
          .localeCompare((valueB as string).toLowerCase())
      }

      return currentSort.direction === 'asc' ? comparison : -comparison
    })

    const fragment = document.createDocumentFragment()

    items.forEach((item) => item.remove())

    sortedItems.forEach((item) => {
      fragment.appendChild(item)
    })

    container.appendChild(fragment)
  }

  function getColumnText(row: HTMLElement, columnIndex: number): string {
    const columns = row.querySelectorAll(
      '[data-column]'
    ) as NodeListOf<HTMLElement>
    if (columns[columnIndex]) {
      return columns[columnIndex].textContent?.trim() || ''
    }
    return ''
  }

  onMount(() => {
    items.forEach((item) => {
      const tile = item.querySelector<HTMLElement>(
        '[data-directional-hover-tile]'
      )
      if (!tile) return

      const handleMouseEnter = (e: MouseEvent) => {
        const dir = getDirection(e, item, type)
        tile.style.transition = 'none'
        tile.style.transform = directionMap[dir] || 'translate(0, 0)'
        void tile.offsetHeight
        tile.style.transition = ''
        tile.style.transform = 'translate(0%, 0%)'
        item.setAttribute('data-status', `enter-${dir}`)

        const hoveredItem = e.currentTarget as HTMLElement

        const mainImage = hoveredItem?.querySelector(
          '[data-column="name"] .work_catalogue_images_wrap img'
        ) as HTMLImageElement

        if (hoverTimeout) {
          clearTimeout(hoverTimeout)
        }

        hoverTimeout = setTimeout(() => {
          if (!hoveredItem.matches(':hover')) {
            return
          }

          // Check if the image is the same as the current active image
          if (activeImage.src === mainImage.src) {
            return
          }

          const existingClone =
            activeImage.parentElement?.querySelector('#mainImage_copy')
          if (existingClone) {
            activeImage.parentElement?.removeChild(existingClone)
          }

          const clone = activeImage.cloneNode(true) as HTMLImageElement
          clone.id = 'mainImage_copy'
          activeImage.parentNode?.appendChild(clone)

          const easeOutQuart = 'cubic-bezier(0.165, 0.84, 0.44, 1)'
          const easeInOutCubic = 'cubic-bezier(0.65, 0, 0.35, 1)'

          if (activeImage.parentElement) {
            activeImage.parentElement.style.perspective = '1200px'
          }

          gsap.set(clone, {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            clipPath: 'inset(0 100% 0 0)',
            rotationY: 5,
            scale: 1.03,
            zIndex: 2,
          })

          clone.src = mainImage.src

          const tl = gsap.timeline()

          tl.to(
            activeImage,
            {
              scale: 0.97,
              rotationY: -3,
              duration: 0.8,
              ease: easeInOutCubic,
            },
            0
          )

          tl.to(
            clone,
            {
              clipPath: 'inset(0 0% 0 0)',
              rotationY: 0,
              scale: 1,
              duration: 0.8,
              ease: easeOutQuart,
            },
            0
          )

          tl.call(
            () => {
              gsap.set(activeImage, {
                scale: 1,
                rotationY: 0,
              })

              activeImage.src = mainImage.src

              if (clone.parentNode) {
                clone.parentNode.removeChild(clone)
              }
            },
            undefined,
            0.85
          )
        }, 300)
      }

      const handleMouseLeave = (e: MouseEvent) => {
        if (hoverTimeout) {
          clearTimeout(hoverTimeout)
          hoverTimeout = null
        }

        const dir = getDirection(e, item, type)
        item.setAttribute('data-status', `leave-${dir}`)
        tile.style.transform = directionMap[dir] || 'translate(0, 0)'
      }

      item.addEventListener('mouseenter', handleMouseEnter)
      item.addEventListener('mouseleave', handleMouseLeave)

      eventHandlers.set(item, {
        enter: handleMouseEnter,
        leave: handleMouseLeave,
      })
    })
    initPagination()
    document.addEventListener('click', function (e: Event) {
      const target = e.target as HTMLElement
      const paginationElement = target.closest(
        '[data-pagination]'
      ) as HTMLElement
      const headerElement = document.querySelector(
        '[data-work-archive-header]'
      ) as HTMLElement

      const headerElementHeight = headerElement.getBoundingClientRect().height

      if (paginationElement && paginationElement.dataset.pagination) {
        setTimeout(() => {
          scrollTo({
            top: headerElementHeight,
            behavior: 'smooth',
          })
        }, 300)
        const direction = paginationElement.dataset.pagination
        paginationControl(direction)
      }
    })

    initTableSorting()
  })

  onDestroy(() => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout)
      hoverTimeout = null
    }

    eventHandlers.forEach((handlers, item) => {
      item.removeEventListener('mouseenter', handlers.enter)
      item.removeEventListener('mouseleave', handlers.leave)
    })
    eventHandlers.clear()
  })
}
